import type { Command } from '@o2d/history';
import { withPatch } from '@o2d/shared';
import type { BoardState } from '../../core/state/board-state.js';
import type { ListType } from './textbox.interface.js';
import type { TextDocument } from './text-document.type.js';
import { isTextBox } from './textbox.types.js';

const EMPTY_DOCUMENT: TextDocument = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

/**
 * Atomically update both `listType` and `document` on a TextBox.
 *
 * Switching list types requires a full document re-shape (paragraphs
 * <-> bulletList/orderedList/taskList nodes). Bundling both changes into a
 * single command keeps Ctrl+Z atomic per PRD-RTE-TIPTAP-document-model §T2.7.
 */
export class SetListTypeCommand implements Command<BoardState> {
  readonly label = 'Listentyp ändern';

  private prevDocument: TextDocument = EMPTY_DOCUMENT;
  private prevListType: ListType = 'none';

  constructor(
    private readonly id: string,
    private readonly newListType: ListType,
    private readonly newDocument: TextDocument,
  ) {}

  apply(state: BoardState): void {
    const obj = state.getObject(this.id);
    if (!obj || !isTextBox(obj)) return;
    this.prevDocument = obj.document;
    this.prevListType = obj.listType;
    state.replaceObject(
      this.id,
      withPatch(obj, { document: this.newDocument, listType: this.newListType }),
    );
  }

  revert(state: BoardState): void {
    const obj = state.getObject(this.id);
    if (!obj || !isTextBox(obj)) return;
    state.replaceObject(
      this.id,
      withPatch(obj, { document: this.prevDocument, listType: this.prevListType }),
    );
  }
}
